import { useState } from 'react';

import { Check, Clock, Hourglass, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { useVisibleItemCount } from '@/hooks/use-visible-item-count';
import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { formatDisplayDate } from '@/utils/date-formatters';

import {
  ApprovalStatus,
  ApprovalStepStatus,
  TimeOffRequestApprover,
} from '../../../types/time-off-request';

interface TimeOffApproversWithStatusCellProps {
  approvers: TimeOffRequestApprover[];
  isPast: boolean;
}

const STATUS_ICON: Record<ApprovalStepStatus, typeof Check> = {
  [ApprovalStepStatus.Approved]: Check,
  [ApprovalStepStatus.Declined]: X,
  [ApprovalStepStatus.AwaitsResponse]: Clock,
  [ApprovalStepStatus.Blocked]: Hourglass,
};

const STATUS_ICON_CLASS: Record<ApprovalStepStatus, string> = {
  [ApprovalStepStatus.Approved]: 'text-success',
  [ApprovalStepStatus.Declined]: 'text-destructive',
  [ApprovalStepStatus.AwaitsResponse]: 'text-warning',
  [ApprovalStepStatus.Blocked]: 'text-muted-foreground',
};

function ApproverTooltipContent({ approver }: { approver: TimeOffRequestApprover }) {
  const { t } = useTranslation('authenticated.timeOff');

  const lines: string[] = [];
  if (approver.status === ApprovalStatus.Approved && approver.decidedAt) {
    lines.push(
      t('approvals.approverTooltip.approvedOn', { date: formatDisplayDate(approver.decidedAt) })
    );
  } else if (approver.status === ApprovalStatus.Rejected && approver.decidedAt) {
    lines.push(
      t('approvals.approverTooltip.declinedOn', { date: formatDisplayDate(approver.decidedAt) })
    );
  }
  if (approver.comment) {
    lines.push(t('approvals.approverTooltip.comment', { comment: approver.comment }));
  }

  if (lines.length === 0) {
    const pendingKey =
      approver.stepStatus === ApprovalStepStatus.Blocked
        ? 'approvals.approverTooltip.waitingInQueue'
        : 'approvals.approverTooltip.upNext';
    return <span>{t(pendingKey)}</span>;
  }

  return (
    <ul className="list-disc space-y-0.5 pl-4">
      {lines.map((line, index) => (
        <li key={index} className="break-words">
          {line}
        </li>
      ))}
    </ul>
  );
}

function ApproverChip({ approver }: { approver: TimeOffRequestApprover }) {
  const Icon = STATUS_ICON[approver.stepStatus] ?? Clock;
  return (
    <SimpleTooltip content={<ApproverTooltipContent approver={approver} />}>
      <Badge variant="secondary" className="gap-1.5 whitespace-nowrap">
        <EntityAvatar
          image={approver.user.profileImageUrl}
          name={approver.user.name}
          size="xs"
          shouldDownscale={false}
          withBorder
        />
        <span>{approver.user.name}</span>
        <Icon className={cn('h-3.5 w-3.5 shrink-0', STATUS_ICON_CLASS[approver.stepStatus])} />
      </Badge>
    </SimpleTooltip>
  );
}

interface ApproversOverflowPopoverProps {
  approvers: TimeOffRequestApprover[];
  onOpenChange?: (open: boolean) => void;
}

function ApproversOverflowPopover({ approvers, onOpenChange }: ApproversOverflowPopoverProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    onOpenChange?.(value);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        className="outline-none"
        onMouseEnter={() => handleOpenChange(true)}
        onMouseLeave={() => handleOpenChange(false)}
      >
        <Badge variant="secondary" className="cursor-default">{`+${approvers.length}`}</Badge>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-48 p-2"
        align="start"
        onMouseEnter={() => handleOpenChange(true)}
        onMouseLeave={() => handleOpenChange(false)}
      >
        <div className="flex flex-col gap-1">
          {approvers.map(approver => {
            const Icon = STATUS_ICON[approver.stepStatus] ?? Clock;
            return (
              <SimpleTooltip
                key={approver.user.id}
                content={<ApproverTooltipContent approver={approver} />}
                side="right"
              >
                <div className="flex cursor-default items-center gap-2 px-1 py-0.5">
                  <EntityAvatar
                    image={approver.user.profileImageUrl}
                    name={approver.user.name}
                    size="sm"
                    shouldDownscale={false}
                  />
                  <span className="text-sm">{approver.user.name}</span>
                  <Icon
                    className={cn(
                      'ml-1 h-3.5 w-3.5 shrink-0',
                      STATUS_ICON_CLASS[approver.stepStatus]
                    )}
                  />
                </div>
              </SimpleTooltip>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TimeOffApproversWithStatusCell({
  approvers,
  isPast,
}: TimeOffApproversWithStatusCellProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { containerRef, shadowRef, visibleCount } = useVisibleItemCount(approvers.length);
  const hiddenCount = approvers.length - visibleCount;

  if (approvers.length === 0) {
    return <span className={cn('text-muted-foreground', isPast && 'opacity-50')}>{'-'}</span>;
  }

  return (
    <div ref={containerRef} className={cn('relative min-w-0', isPast && 'opacity-50')}>
      {/* Shadow clone for measuring item widths */}
      <div
        ref={shadowRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex gap-2"
      >
        {approvers.map(approver => {
          const Icon = STATUS_ICON[approver.stepStatus] ?? Clock;
          return (
            <Badge key={approver.user.id} variant="secondary" className="gap-1.5 whitespace-nowrap">
              <EntityAvatar
                image={approver.user.profileImageUrl}
                name={approver.user.name}
                size="xs"
                shouldDownscale={false}
                withBorder
              />
              {approver.user.name}
              <Icon className="h-3.5 w-3.5 shrink-0" />
            </Badge>
          );
        })}
      </div>

      <ScrollableText paused={popoverOpen} disabled={hiddenCount > 0}>
        <div className="flex items-center gap-2">
          <div className={cn('flex gap-2', hiddenCount > 0 && 'min-w-0 overflow-hidden')}>
            {approvers.slice(0, visibleCount).map(approver => (
              <ApproverChip key={approver.user.id} approver={approver} />
            ))}
          </div>
          {hiddenCount > 0 && (
            <div className="shrink-0">
              <ApproversOverflowPopover
                approvers={approvers.slice(visibleCount)}
                onOpenChange={setPopoverOpen}
              />
            </div>
          )}
        </div>
      </ScrollableText>
    </div>
  );
}
